import posthog from "posthog-js";
import { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";

/**
 * Analytics service for Spotlight using PostHog
 * PostHog is initialized by PostHogProvider in index.tsx
 * This service provides helper methods for tracking events
 */
class AnalyticsService {
  private readonly APP_NAME = "spotlight";

  /**
   * Check if PostHog is available and initialized
   */
  private isAvailable(): boolean {
    return typeof posthog !== "undefined" && posthog.__loaded;
  }

  /**
   * Identify a user with their organization
   */
  identify(user: any, organization: any, role?: string | null): void {
    if (!this.isAvailable()) return;

    try {
      // Identify the user using ID (privacy-first: no PII as distinct_id)
      posthog.identify(user.id, {
        // User traits (PII OK in properties for server-side processing)
        email: user.email,
        name: `${user.first_name} ${user.last_name}`.trim(),
        // Always include organization context
        organization_id: organization.id,
        organization_name: organization.name,
        plan_type: organization.plan_type,
        role: role || "member",
        app: this.APP_NAME,
      });

      // Set organization as a group for cohort analysis
      posthog.group("organization", organization.id, {
        name: organization.name,
        plan_type: organization.plan_type,
        currency: organization.currency,
      });
    } catch (error) {
      console.error("Failed to identify user:", error);
    }
  }

  /**
   * Reset user identification on logout
   */
  reset(): void {
    if (!this.isAvailable()) return;

    try {
      posthog.reset();
    } catch (error) {
      console.error("Failed to reset analytics:", error);
    }
  }

  /**
   * Track a custom event
   */
  track(event: string, properties?: Record<string, any>): void {
    if (!this.isAvailable()) return;

    try {
      posthog.capture(event, {
        app: this.APP_NAME,
        ...properties,
      });
    } catch (error) {
      console.error("Failed to track event:", error);
    }
  }

  /**
   * Track a page view
   */
  trackPageView(path: string, title?: string): void {
    if (!this.isAvailable()) return;

    try {
      posthog.capture("$pageview", {
        $current_url: window.location.href,
        $pathname: path,
        $title: title || document.title,
        app: this.APP_NAME,
      });
    } catch (error) {
      console.error("Failed to track page view:", error);
    }
  }

  /**
   * Track a business event with standardized naming
   */
  trackBusinessEvent(
    entity: string,
    action: string,
    metadata?: Record<string, any>,
  ): void {
    const event = `${this.APP_NAME}.${entity}.${action}`;
    this.track(event, metadata);
  }

  /**
   * Track API request (for interceptor)
   */
  trackApiRequest(config: AxiosRequestConfig): void {
    // Only track write operations
    if (
      !config.method ||
      ["get", "head", "options"].includes(config.method.toLowerCase())
    ) {
      return;
    }

    this.track("api_request", {
      method: config.method.toUpperCase(),
      path: config.url,
      has_mcp_header: !!config.headers?.["X-MCP-Client"],
    });
  }

  /**
   * Track API response (for interceptor)
   */
  trackApiResponse(response: AxiosResponse, startTime: number): void {
    const duration = Date.now() - startTime;

    // Only track slow requests or errors
    if (duration > 1000 || response.status >= 400) {
      this.track("api_response", {
        method: response.config.method?.toUpperCase(),
        path: response.config.url,
        status: response.status,
        duration_ms: duration,
        is_slow: duration > 1000,
        is_error: response.status >= 400,
      });
    }
  }

  /**
   * Track API error (for interceptor)
   */
  trackApiError(error: AxiosError): void {
    this.track("api_error", {
      method: error.config?.method?.toUpperCase(),
      path: error.config?.url,
      status: error.response?.status,
      message: error.message,
    });
  }

  /**
   * Set user properties that persist across events
   */
  setUserProperties(properties: Record<string, any>): void {
    if (!this.isAvailable()) return;

    try {
      posthog.people.set(properties);
    } catch (error) {
      console.error("Failed to set user properties:", error);
    }
  }

  /**
   * Track a feature flag evaluation
   */
  getFeatureFlag(flag: string): boolean | string | undefined {
    if (!this.isAvailable()) return undefined;

    try {
      return posthog.getFeatureFlag(flag);
    } catch (error) {
      console.error("Failed to get feature flag:", error);
      return undefined;
    }
  }
}

// Export singleton instance
const analytics = new AnalyticsService();
export default analytics;
